import React, { useEffect, useState } from "react";
import { fetchProtectedBlobUrl } from "@/lib/protectedFile";

/**
 * Displays a Bearer-protected image inline by fetching it as a blob.
 * Never uses a raw URL in `<img src>`. Revokes object-URL on unmount.
 *
 * Props:
 *   path      required — API path e.g. `/files/abc123` or `/portal/files/abc123`
 *   alt       required
 *   className optional
 *   testId    optional
 *   onError   optional — called with the error object
 */
export function ProtectedImage({ path, alt, className, testId, onError }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!path) { setSrc(null); return; }
    setErr(null);
    fetchProtectedBlobUrl(path)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setSrc(u);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e);
          if (onError) onError(e);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, onError]);

  if (err) {
    return (
      <div data-testid={testId ? `${testId}-error` : undefined}
           className="border-2 border-dashed border-red-200 bg-red-50 rounded p-4 text-center text-xs text-red-700">
        Could not load image ({err.status || "network"})
      </div>
    );
  }
  if (!src) {
    return (
      <div data-testid={testId ? `${testId}-loading` : undefined}
           className="border-2 border-dashed border-slate-200 rounded p-6 text-center text-xs text-slate-500 animate-pulse">
        Loading photo…
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} data-testid={testId} />;
}
